import React, { useState } from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import Navbar from "../components/Navbar";
import PhotoUploader from "../components/PhotoUploader";
import categories from "../data/categories";
import cities from "../data/cities";
import { sendNewVendorAlert } from "../lib/email";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../i18n/LanguageContext";
import usePageMeta from "../hooks/usePageMeta";
import usePlanSettings, { priceFor, fmtPrice } from "../hooks/usePlanSettings";
import PricePicker from "../components/PricePicker";

const EMPTY = {
  businessName: "",
  category: "",
  accountType: "company",
  city: "",
  priceRange: "€€",
  priceMode: "none",
  priceMin: "",
  priceMax: "",
  priceFrom: "",
  phone: "",
  whatsapp: "",
  instagram: "",
  description: "",
  email: "",
  password: "",
  tier: "basic",
  videoUrl: "",
  website: "",
  extraCategories: [],
};

const STEPS = 4;

export default function ListBusiness() {
  const { t, lang } = useLang();
  const { ensureAccount } = useAuth();
  const { settings: plans, founding } = usePlanSettings();
  usePageMeta(t("meta.list"));
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(EMPTY);
  const [photos, setPhotos] = useState([]);
  const [sameWhatsapp, setSameWhatsapp] = useState(false);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const activeCat = categories.find((c) => c.id === form.category);
  const allowIndividual = activeCat ? activeCat.allowIndividual : false;

  const set = (key, value) =>
    setForm((f) => {
      const next = { ...f, [key]: value };
      if (key === "category") {
        const cat = categories.find((c) => c.id === value);
        if (cat && !cat.allowIndividual) next.accountType = "company";
      }
      if (key === "phone" && sameWhatsapp) next.whatsapp = value.replace(/\D/g, "");
      return next;
    });

  const toggleSameWhatsapp = (checked) => {
    setSameWhatsapp(checked);
    if (checked) set("whatsapp", form.phone.replace(/\D/g, ""));
  };

  const validateStep = () => {
    if (step === 0 && (!form.businessName || !form.category || !form.city)) return false;
    if (step === 1 && !form.phone) return false;
    return true;
  };

  const next = () => {
    if (!validateStep()) {
      setError(t("list.required"));
      return;
    }
    setError("");
    setStep((s) => Math.min(s + 1, STEPS - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const back = () => {
    setError("");
    setStep((s) => Math.max(s - 1, 0));
  };

  const submit = async () => {
    setError("");
    if (!form.email || !form.password) {
      setError(t("list.required"));
      return;
    }
    if (form.password.length < 6) {
      setError(t("list.weakPassword"));
      return;
    }
    setSending(true);
    try {
      // 1) Create (or sign into) the vendor's account.
      const user = await ensureAccount(form.email.trim(), form.password);

      // 2) Create the listing tied to that account.
      const premium = form.tier === "premium";
      const payload = {
        businessName: form.businessName,
        category: form.category,
        accountType: form.accountType,
        city: form.city,
        priceRange: form.priceMode === "tier" ? form.priceRange : "",
        priceMode: form.priceMode,
        priceMin: form.priceMode === "range" ? form.priceMin : "",
        priceMax: form.priceMode === "range" ? form.priceMax : "",
        priceFrom: form.priceMode === "from" ? form.priceFrom : "",
        phone: form.phone,
        whatsapp: sameWhatsapp ? form.phone.replace(/\D/g, "") : form.whatsapp,
        instagram: form.instagram,
        description: form.description,
        videoUrl: premium ? form.videoUrl : "",
        website: premium ? form.website : "",
        extraCategories: premium ? form.extraCategories : [],
      };
      await addDoc(collection(db, "vendors"), {
        ...payload,
        photos,
        ownerUid: user.uid,
        ownerEmail: form.email.trim(),
        isActive: false,
        status: "pending",
        tier: form.tier,
        createdAt: serverTimestamp(),
      });

      // 3) Notify admin (non-blocking).
      try {
        await sendNewVendorAlert(payload);
      } catch (e) {
        console.warn("Alert email failed:", e.message || e);
      }

      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      console.error("Registration failed:", err.code || "", err.message || err);
      if (err.code === "auth/invalid-email") setError(t("list.invalidEmail"));
      else if (err.code === "auth/weak-password") setError(t("list.weakPassword"));
      else if (err.code === "auth/wrong-password" || err.code === "auth/invalid-credential")
        setError(t("list.emailInUse"));
      else setError(t("list.uploadError"));
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return (
      <>
        <Navbar />
        <Success>
          <h1>{t("list.successTitle")}</h1>
          <p>{t("list.successText")}</p>
          <Link to="/manage">{t("list.goManage")}</Link>
          <Link to="/" className="ghost">{t("list.backHome")}</Link>
        </Success>
      </>
    );
  }

  const stepTitles = [t("list.step1Title"), t("list.step2Title"), t("list.step3Title"), t("list.step4Title")];

  return (
    <>
      <Navbar />
      <Wrap>
        <Head>
          <h1>{t("list.title")}</h1>
          <p>{t("list.subtitle")}</p>
        </Head>

        <Progress>
          <Track>
            <Fill style={{ width: `${((step + 1) / STEPS) * 100}%` }} />
          </Track>
          <Label>
            {t("list.step")} {step + 1} {t("list.of")} {STEPS} — {stepTitles[step]}
          </Label>
        </Progress>

        {step === 0 && (
          <StepBox>
            <Field>
              <label>{t("list.businessName")} *</label>
              <input value={form.businessName} onChange={(e) => set("businessName", e.target.value)} />
            </Field>
            <Row>
              <Field>
                <label>{t("list.category")} *</label>
                <select value={form.category} onChange={(e) => set("category", e.target.value)}>
                  <option value="">{t("list.choose")}</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>{c[lang]}</option>
                  ))}
                </select>
              </Field>
              <Field>
                <label>{t("list.city")} *</label>
                <select value={form.city} onChange={(e) => set("city", e.target.value)}>
                  <option value="">{t("list.choose")}</option>
                  {cities.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </Field>
            </Row>
            {allowIndividual && (
              <Field>
                <label>{t("list.accountType")}</label>
                <Segmented>
                  <button type="button" className={form.accountType === "company" ? "on" : ""} onClick={() => set("accountType", "company")}>{t("list.company")}</button>
                  <button type="button" className={form.accountType === "individual" ? "on" : ""} onClick={() => set("accountType", "individual")}>{t("list.individual")}</button>
                </Segmented>
              </Field>
            )}
            <Field>
              <label>{t("price.label")}</label>
              <PricePicker value={form} onChange={set} />
            </Field>
          </StepBox>
        )}

        {step === 1 && (
          <StepBox>
            <Field>
              <label>{t("list.phone")} *</label>
              <input value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder={t("list.phoneHint")} />
            </Field>
            <Check>
              <input type="checkbox" checked={sameWhatsapp} onChange={(e) => toggleSameWhatsapp(e.target.checked)} />
              {t("list.sameWhatsapp")}
            </Check>
            <Field>
              <label>{t("list.whatsapp")}</label>
              <input value={form.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} placeholder={t("list.whatsappHint")} disabled={sameWhatsapp} />
            </Field>
            <Field>
              <label>{t("list.instagram")}</label>
              <input value={form.instagram} onChange={(e) => set("instagram", e.target.value)} placeholder={t("list.instagramHint")} />
            </Field>
          </StepBox>
        )}

        {step === 2 && (
          <StepBox>
            <Field>
              <label>{t("list.description")}</label>
              <textarea rows={4} maxLength={1000} value={form.description} onChange={(e) => set("description", e.target.value)} />
            </Field>
            <Field>
              <label>{t("list.photos")} <small>{t("list.photosHint")}</small></label>
              <PhotoUploader photos={photos} setPhotos={setPhotos} max={20} />
            </Field>

            <AccountBox>
              <h3>{t("list.account")}</h3>
              <p>{t("list.accountHint")}</p>
              <Row>
                <Field>
                  <label>{t("list.email")} *</label>
                  <input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} />
                </Field>
                <Field>
                  <label>{t("list.password")} *</label>
                  <input type="password" value={form.password} onChange={(e) => set("password", e.target.value)} />
                </Field>
              </Row>
            </AccountBox>
          </StepBox>
        )}

        {step === 3 && (
          <StepBox>
            <PlanIntro>{t("plan.intro")}</PlanIntro>
            <Plans>
              {["basic", "premium"].map((tier) => {
                const on = form.tier === tier;
                const feats =
                  tier === "basic"
                    ? [t("plan.b1"), t("plan.b2"), t("plan.b3")]
                    : [t("plan.p1"), t("plan.p2"), t("plan.p3"), t("plan.p4"), t("plan.p5"), t("plan.p6")];
                return (
                  <Plan key={tier} type="button" $on={on} $premium={tier === "premium"} onClick={() => set("tier", tier)}>
                    {tier === "premium" && <Ribbon>{t("plan.popular")}</Ribbon>}
                    <h3>{t(`plan.${tier}`)}</h3>
                    <Price>
                      {fmtPrice(priceFor(plans, form.category, tier, founding))}
                      <small>/{t("plan.mo")}</small>
                    </Price>
                    <ul>
                      {feats.map((f, i) => (
                        <li key={i}>{f}</li>
                      ))}
                    </ul>
                    <Radio $on={on}>{on ? `✓ ${t("plan.selected")}` : t("plan.select")}</Radio>
                  </Plan>
                );
              })}
            </Plans>

            {form.tier === "premium" && (
              <PremiumExtras>
                <h4>{t("plan.extrasTitle")}</h4>
                <Field>
                  <label>{t("plan.video")} <small>{t("plan.videoHint")}</small></label>
                  <input value={form.videoUrl} onChange={(e) => set("videoUrl", e.target.value)} placeholder="https://youtube.com/..." />
                </Field>
                <Field>
                  <label>{t("plan.website")}</label>
                  <input value={form.website} onChange={(e) => set("website", e.target.value)} placeholder="https://..." />
                </Field>
                <Field>
                  <label>{t("plan.extraCats")} <small>{t("plan.extraCatsHint")}</small></label>
                  <CatGrid>
                    {categories
                      .filter((c) => c.id !== form.category)
                      .map((c) => {
                        const sel = form.extraCategories.includes(c.id);
                        const full = form.extraCategories.length >= 2 && !sel;
                        return (
                          <CatChip
                            key={c.id}
                            type="button"
                            $on={sel}
                            disabled={full}
                            onClick={() =>
                              set(
                                "extraCategories",
                                sel
                                  ? form.extraCategories.filter((x) => x !== c.id)
                                  : [...form.extraCategories, c.id]
                              )
                            }
                          >
                            {c[lang]}
                          </CatChip>
                        );
                      })}
                  </CatGrid>
                </Field>
              </PremiumExtras>
            )}

            {founding && <FoundingNote>✦ {t("plan.foundingNote")}</FoundingNote>}
          </StepBox>
        )}

        {error && <ErrorMsg>{error}</ErrorMsg>}

        <Nav>
          {step > 0 && <Ghost onClick={back}>← {t("list.back")}</Ghost>}
          {step < STEPS - 1 ? (
            <Primary onClick={next}>{t("list.next")} →</Primary>
          ) : (
            <Primary onClick={submit} disabled={sending}>
              {sending ? t("list.submitting") : t("list.submit")}
            </Primary>
          )}
        </Nav>
      </Wrap>
    </>
  );
}

const Wrap = styled.div`
  max-width: 680px;
  margin: 0 auto;
  padding: clamp(30px, 6vw, 56px) clamp(20px, 5vw, 40px) 80px;
  display: flex;
  flex-direction: column;
  gap: 22px;
`;

const Head = styled.div`
  h1 { font-family: ${({ theme }) => theme.font.display}; font-weight: 500; font-size: clamp(28px, 5vw, 44px); color: ${({ theme }) => theme.colors.ink}; }
  p { margin-top: 10px; color: ${({ theme }) => theme.colors.inkSoft}; }
`;

const Progress = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Track = styled.div`
  height: 6px;
  background: ${({ theme }) => theme.colors.creamDeep};
  border-radius: 100px;
  overflow: hidden;
`;

const Fill = styled.div`
  height: 100%;
  background: ${({ theme }) => theme.colors.green};
  border-radius: 100px;
  transition: width 0.35s cubic-bezier(.2,.7,.2,1);
`;

const Label = styled.span`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.inkSoft};
`;

const StepBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 7px;
  flex: 1;
  label { font-size: 13.5px; font-weight: 500; color: ${({ theme }) => theme.colors.ink}; small { font-weight: 400; color: ${({ theme }) => theme.colors.inkSoft}; } }
  input, select, textarea {
    font-family: inherit; font-size: 15px; color: ${({ theme }) => theme.colors.ink};
    background: ${({ theme }) => theme.colors.surface}; border: 1px solid ${({ theme }) => theme.colors.hair};
    border-radius: 12px; padding: 12px 14px; outline: none; transition: border-color 0.18s;
  }
  input:focus, select:focus, textarea:focus { border-color: ${({ theme }) => theme.colors.green}; }
  input:disabled { opacity: 0.55; }
  textarea { resize: vertical; }
`;

const Row = styled.div`
  display: flex;
  gap: 16px;
  @media (max-width: 560px) { flex-direction: column; }
`;

const Check = styled.label`
  display: flex;
  align-items: center;
  gap: 9px;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.inkSoft};
  cursor: pointer;
  input { width: 17px; height: 17px; accent-color: ${({ theme }) => theme.colors.green}; }
`;

const Segmented = styled.div`
  display: inline-flex;
  background: ${({ theme }) => theme.colors.creamDeep};
  border-radius: 12px;
  padding: 4px;
  width: fit-content;
  button { border: none; background: transparent; cursor: pointer; font-size: 14px; font-weight: 500; color: ${({ theme }) => theme.colors.inkSoft}; padding: 9px 20px; border-radius: 9px; transition: 0.18s; }
  button.on { background: ${({ theme }) => theme.colors.green}; color: ${({ theme }) => theme.colors.cream}; }
`;

const AccountBox = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.hair};
  border-radius: 14px;
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  h3 { font-family: ${({ theme }) => theme.font.display}; font-weight: 500; font-size: 19px; }
  p { font-size: 13.5px; color: ${({ theme }) => theme.colors.inkSoft}; margin-top: -4px; }
`;

const PlanIntro = styled.p`
  font-size: 15px;
  color: ${({ theme }) => theme.colors.inkSoft};
`;

const Plans = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  @media (max-width: 560px) { grid-template-columns: 1fr; }
`;

const Plan = styled.button`
  position: relative;
  text-align: left;
  background: ${({ theme }) => theme.colors.surface};
  border: 1.5px solid ${({ $on, theme }) => ($on ? theme.colors.green : theme.colors.hair)};
  box-shadow: ${({ $on }) => ($on ? "0 6px 22px rgba(47,74,60,.12)" : "none")};
  border-radius: 18px;
  padding: 22px 20px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 12px;
  transition: 0.2s;
  &:hover { border-color: ${({ theme }) => theme.colors.green}; }
  h3 { font-family: ${({ theme }) => theme.font.display}; font-weight: 500; font-size: 22px; color: ${({ theme }) => theme.colors.ink}; }
  ul { list-style: none; display: flex; flex-direction: column; gap: 9px; margin-top: 2px; }
  li { font-size: 13.5px; color: ${({ theme }) => theme.colors.inkSoft}; padding-left: 22px; position: relative; line-height: 1.4; }
  li::before { content: "✓"; position: absolute; left: 0; color: ${({ theme }) => theme.colors.green}; font-weight: 700; }
`;

const Ribbon = styled.span`
  position: absolute;
  top: 16px;
  right: 16px;
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.greenDeep};
  background: ${({ theme }) => theme.colors.gold};
  padding: 4px 10px;
  border-radius: 100px;
`;

const Price = styled.div`
  font-family: ${({ theme }) => theme.font.display};
  font-size: 34px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.green};
  small { font-family: ${({ theme }) => theme.font.body}; font-size: 14px; font-weight: 400; color: ${({ theme }) => theme.colors.inkSoft}; margin-left: 2px; }
`;

const Radio = styled.div`
  margin-top: auto;
  text-align: center;
  font-size: 14px;
  font-weight: 500;
  padding: 11px;
  border-radius: 100px;
  color: ${({ $on, theme }) => ($on ? theme.colors.cream : theme.colors.green)};
  background: ${({ $on, theme }) => ($on ? theme.colors.green : theme.colors.creamDeep)};
`;

const PremiumExtras = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.gold};
  border-radius: 16px;
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  h4 { font-family: ${({ theme }) => theme.font.display}; font-weight: 500; font-size: 18px; }
`;

const CatGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const CatChip = styled.button`
  font-size: 13px;
  padding: 8px 14px;
  border-radius: 100px;
  cursor: pointer;
  border: 1px solid ${({ $on, theme }) => ($on ? theme.colors.green : theme.colors.hair)};
  background: ${({ $on, theme }) => ($on ? theme.colors.green : "transparent")};
  color: ${({ $on, theme }) => ($on ? theme.colors.cream : theme.colors.inkSoft)};
  transition: 0.18s;
  &:disabled { opacity: 0.4; cursor: default; }
`;

const FoundingNote = styled.p`
  text-align: center;
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.green};
  background: rgba(201, 182, 142, 0.18);
  padding: 12px;
  border-radius: 12px;
`;

const ErrorMsg = styled.p`
  color: ${({ theme }) => theme.colors.rose};
  font-size: 14px;
`;

const Nav = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-top: 8px;
`;

const Ghost = styled.button`
  background: transparent; border: 1px solid ${({ theme }) => theme.colors.hair};
  border-radius: 100px; padding: 14px 26px; font-size: 15px; font-weight: 500;
  color: ${({ theme }) => theme.colors.inkSoft}; cursor: pointer;
  &:hover { border-color: ${({ theme }) => theme.colors.green}; color: ${({ theme }) => theme.colors.ink}; }
`;

const Primary = styled.button`
  margin-left: auto; background: ${({ theme }) => theme.colors.green}; color: ${({ theme }) => theme.colors.cream};
  border: none; border-radius: 100px; font-size: 15px; font-weight: 500; padding: 14px 34px; cursor: pointer; transition: 0.25s;
  &:hover { background: ${({ theme }) => theme.colors.greenDeep}; }
  &:disabled { opacity: 0.6; cursor: default; }
`;

const Success = styled.div`
  min-height: 70vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 12px;
  padding: 40px;
  h1 { font-family: ${({ theme }) => theme.font.display}; font-weight: 500; font-size: clamp(32px, 6vw, 52px); color: ${({ theme }) => theme.colors.green}; }
  p { max-width: 420px; color: ${({ theme }) => theme.colors.inkSoft}; }
  a { color: ${({ theme }) => theme.colors.cream}; background: ${({ theme }) => theme.colors.green}; padding: 13px 28px; border-radius: 100px; font-weight: 500; margin-top: 8px; }
  a.ghost { background: transparent; color: ${({ theme }) => theme.colors.green}; padding: 4px; }
`;
